import React, { Fragment, memo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  useMediaQuery,
} from "@mui/material";
import { ExpandLess, ExpandMore, Menu } from "@mui/icons-material";
import { styled, useTheme } from "@mui/material/styles";
import Images from "../assets/Images";
import colors from "../styles/colors";
import Navigation from "../Navigation";
import SNavigation from "../SNavigation";

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(1, 0, 0, 1),
  margin: "10px",
  ...theme.mixins.toolbar,
}));

const drawerWidth = 270;

const SideNav = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [expand, setExpand] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleToggleSubMenu = (index) => {
    setExpand((prevState) => {
      const updated = [...prevState];
      updated[index] = !updated[index];
      return updated;
    });
  };

  const role = localStorage.getItem("role");

  const menuData = role === "A" ? Navigation : SNavigation;

  const drawerContent = (
    <Box sx={{ textAlign: "center" }}>
      <DrawerHeader>
        <img src={Images.logo} alt="Takseem" width="70%" />
      </DrawerHeader>
      <List>
        {menuData.map((item, index) =>
          item.subMenu ? (
            <Fragment key={index}>
              <ListItem
                button
                onClick={() => handleToggleSubMenu(index)}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  mt: 1.5,
                  width: "94%",
                  "&.Mui-selected": {
                    backgroundImage: colors.primaryGradient,
                    bgcolor: colors.primary,
                    color: colors.white,
                  },
                  ":hover": {
                    backgroundColor: colors.primary,
                    color: colors.white,
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: 4,
                    justifyContent: "center",
                    bgcolor: colors.white,
                    borderRadius: "6px",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} />
                {expand[index] ? <ExpandLess /> : <ExpandMore />}
              </ListItem>
              <Collapse in={expand[index]}>
                <List>
                  {item.subMenu.map((subItem, subIndex) => (
                    <ListItem
                      key={subIndex}
                      button
                      selected={pathname === subItem.path}
                      onClick={() => {
                        navigate(subItem.path);
                        if (isMobile) setMobileOpen(false); // close drawer on mobile
                      }}
                      sx={{
                        borderRadius: 2,
                        width: "94%",
                        mx: 1,
                        mt: 1.5,
                        "&.Mui-selected": {
                          backgroundImage: colors.primaryGradient,
                          bgcolor: colors.primary,
                          color: colors.white,
                        },
                        ":hover": {
                          backgroundColor: colors.primary,
                          color: colors.white,
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          ml: 2,
                          mr: 4,
                          justifyContent: "center",
                          bgcolor: colors.white,
                          borderRadius: "6px",
                        }}
                      >
                        {subItem.icon}
                      </ListItemIcon>
                      <ListItemText primary={subItem.label} />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Fragment>
          ) : (
            <ListItem key={index} disablePadding>
              <ListItemButton
                selected={
                  pathname === item.path ||
                  pathname === item.subPath
                }
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false); // close drawer on mobile
                }}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  mt: 1.5,
                  "&.Mui-selected": {
                    backgroundImage: colors.primaryGradient,
                    bgcolor: colors.primary,
                    color: colors.white,
                  },
                  ":hover": {
                    backgroundColor: colors.primary,
                    color: colors.white,
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: 4,
                    justifyContent: "center",
                    bgcolor: colors.white,
                    borderRadius: "6px",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          )
        )}
      </List>
    </Box>
  );

  return (
    <>
      {/* Menu icon only visible on small screens */}
      {isMobile && (
        <IconButton
          onClick={() => setMobileOpen(!mobileOpen)}
          sx={{
            position: "fixed",
            top: 16,
            left: 16,
            zIndex: 1300,
            bgcolor: colors.primary,
            color: "#fff",
            "&:hover": { bgcolor: colors.primaryDark },
          }}
        >
          <Menu />
        </IconButton>
      )}

      {/* Drawer — responsive */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            bgcolor: colors.white,
            overflowY: "scroll",
            "&::-webkit-scrollbar": {
              width: 6,
              bgcolor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 1,
              bgcolor: colors.primary,
            },
          },
        }}
        anchor="left"
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default memo(SideNav);
